import { useLayoutEffect, useState } from "react";
import { apiMutate } from "../hooks/useApi";
import { Button, ErrorNote, Icon, Input, Modal, Textarea } from "./components";

/**
 * Posteingang S1, Entscheidung P1 (Antoine 26.09.2026): eine Anfrage aus einer Mail bekommt die
 * Eingangsbestätigung als vorbefüllten, wegklickbaren Dialog — dasselbe Muster wie „Telefonisch
 * angenommen“. Der Entwurf kommt vom Server (`create-inquiry` → `bestaetigung`, dieselbe Vorlage
 * wie beim Formular) und fragt fehlende Angaben gleich mit ab. Versendet wird NUR über den Knopf
 * „Senden“ (POST /api/events/:id/reply — der Klick ist die Freigabe), nie automatisch.
 */
export interface BestaetigungsEntwurf {
  subject: string;
  text: string;
  toAddr: string;
  fehlend: string[];
}

const FEHLEND_TEXT: Record<string, string> = { eventDate: "Datum", guests: "Gästezahl", serviceKind: "im Haus oder Catering" };

export function EingangsbestaetigungDialog({
  open,
  eventId,
  entwurf,
  onFertig,
}: {
  open: boolean;
  eventId: string;
  entwurf: BestaetigungsEntwurf;
  /** `gesendet` = true nur nach erfolgreichem Versand; false = weggeklickt. */
  onFertig: (gesendet: boolean) => void;
}) {
  const [subject, setSubject] = useState(entwurf.subject);
  const [text, setText] = useState(entwurf.text);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (open) {
      setSubject(entwurf.subject);
      setText(entwurf.text);
      setFehler(null);
      setBusy(false);
    }
  }, [open, entwurf]);

  async function senden() {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate(`/api/events/${eventId}/reply`, "POST", { channel: "email", subject, text });
      onFertig(true);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Die Bestätigung konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => onFertig(false)}
      width="560px"
      title="Anfrage angelegt — Bestätigung senden?"
      subtitle={<>An {entwurf.toAddr}</>}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={() => onFertig(false)} disabled={busy}>
            Nicht senden
          </Button>
          <Button type="button" data-testid="bestaetigung-senden" onClick={() => void senden()} disabled={busy || !subject.trim() || !text.trim()}>
            <Icon name="send" className="text-[16px]" /> {busy ? "Wird gesendet …" : "Senden"}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        {entwurf.fehlend.length > 0 && (
          <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-3 py-2 text-[12px] text-[#6F675A]">
            <Icon name="help" className="mt-0.5 text-[14px]" />
            <span>Rückfrage steht schon im Text: {entwurf.fehlend.map((f) => FEHLEND_TEXT[f] ?? f).join(", ")}.</span>
          </p>
        )}
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Betreff</span>
          <Input data-testid="bestaetigung-betreff" className="!py-2.5" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={300} disabled={busy} />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Nachricht</span>
          <Textarea
            data-testid="bestaetigung-nachricht"
            className="min-h-[200px] text-[12.5px] leading-relaxed"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={20000}
            disabled={busy}
          />
          <p className="mt-1 text-[10.5px] text-secondary-fixed-dim">Gruß und Signatur hängt MAESTRO beim Versand an. Ohne Klick auf „Senden“ geht nichts raus.</p>
        </label>
        {fehler ? <ErrorNote message={fehler} /> : null}
      </div>

    </Modal>
  );
}
